// /src/Backoffice/pages/quotedetail.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";
import "./quotedetail.css";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");

const API_QUOTE_DETAIL = (id) => `${API_BASE}/superuser/quotes/${id}/`;
const API_QUOTE_STATUS = (id) => `${API_BASE}/superuser/quotes/${id}/status/`;
const API_QUOTE_PRICING = (id) => `${API_BASE}/superuser/quotes/${id}/pricing/`;
const API_PENNYLANE_ESTIMATE = (id) => `${API_BASE}/superuser/quotes/${id}/pennylane-estimate/`;
const API_ORDER_CONFIRMATION_FILE = (id) => `${API_BASE}/superuser/quotes/${id}/order-confirmation-file/`;
const API_BANK_TRANSFER_MARK_PAID = (id) => `${API_BASE}/superuser/quotes/${id}/bank-transfer/mark-paid/`;
const API_PENNYLANE_INVOICE_SEND = (id) => `${API_BASE}/superuser/quotes/${id}/pennylane-invoice/send/`;
const API_PENNYLANE_MANUAL_PAID_SENT = (id) => `${API_BASE}/superuser/quotes/${id}/pennylane-invoice/manual-paid-sent/`;

function eur(n) {
  const x = Number(n || 0);
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(x);
}
function eur3(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(x);
}
function pctFromRatio(r) {
  const v = Number(r || 0);
  return (v * 100).toFixed(2);
}
function parseDecimalInput(v, fallback = 0) {
  if (v === null || v === undefined) return fallback;
  const normalized = String(v).trim().replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}
function normalizeStatus(s) {
  return String(s || "draft").toLowerCase();
}
function badgeClass(status) {
  const s = normalizeStatus(status);
  if (s === "validated") return "boBadge boBadgeOk";
  if (s === "paid") return "boBadge boBadgePaid";
  if (s === "in_progress") return "boBadge boBadgeInfo";
  if (s === "delivered") return "boBadge boBadgeNeutral";
  return "boBadge";
}

function invoicePaymentStatusLabel(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "paid") return "payée";
  if (value === "to_pay") return "à payer";
  if (value === "manual_paid_invoice_required") return "envoi manuel requis";
  if (value === "manual_paid_invoice_sent") return "envoyée manuellement";
  if (!value) return "";
  return value.replaceAll("_", " ");
}

// fallback only if backend does NOT return lines
function buildLinesFromMeta(q) {
  const m = q?.pricing_meta || q?.meta;
  if (!m) return [];
  const out = [];
  const inv = m.invoice_like || {};
  const totals = inv.totals || {};
  if (totals?.pcb != null) out.push({ label: "Fabrication PCB", kind: "pcb", amount: totals.pcb });
  if (totals?.assembly != null) out.push({ label: "Assemblage", kind: "assembly", amount: totals.assembly });
  if (totals?.components_kit != null) out.push({ label: "Composants", kind: "components", amount: totals.components_kit });
  if (totals?.shipping != null) out.push({ label: "Expédition", kind: "shipping", amount: totals.shipping });
  return out;
}

function typeFromLabel(label) {
  const s = String(label || "").trim().toLowerCase();
  if (s.includes("pcb") || s.includes("fabrication")) return "pcb";
  if (s.includes("assembl") || s.includes("frais")) return "assembly";
  if (s.includes("compos") || s.includes("component")) return "components";
  if (s.includes("exped") || s.includes("livr") || s.includes("shipp")) return "shipping";
  return "-";
}

function hasMeaningfulValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function humanizeKey(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function flattenDisplayEntries(value, prefix = "") {
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) => flattenDisplayEntries(entry, prefix ? `${prefix} ${index + 1}` : `${index + 1}`));
  }
  if (value && typeof value === "object") {
    return Object.entries(value).flatMap(([key, nested]) =>
      flattenDisplayEntries(nested, prefix ? `${prefix} - ${humanizeKey(key)}` : humanizeKey(key))
    );
  }
  if (!hasMeaningfulValue(value)) return [];
  return [[prefix || "Valeur", typeof value === "boolean" ? (value ? "Oui" : "Non") : String(value)]];
}

function pcbSpecEntries(spec) {
  if (!spec) return [];
  const baseEntries = [
    ["Mode PCB", spec.client_pcb_mode],
    ["Controle impedance", spec.impedance_control === null || spec.impedance_control === undefined ? "" : spec.impedance_control ? "Oui" : "Non"],
    ["Family", spec.family],
    ["Layers", spec.layers],
    ["Legend", spec.legend],
    ["Size X", spec.size_x],
    ["Size Y", spec.size_y],
    ["Material", spec.material],
    ["Position", spec.position],
    ["Supplier", spec.supplier],
    ["Color Bot", spec.solder_color_bot],
    ["Color Top", spec.solder_color_top],
    ["Ipc Class", spec.ipc_class],
    ["Thickness", spec.thickness],
    ["Date Position", spec.date_position],
    ["Stackup Method", spec.stackup_method || spec.stackup_type],
    ["Surface Finish", spec.surface_finish || spec.finish],
    ["Copper External", spec.external_copper_thickness],
    ["Copper Internal", spec.inner_copper_thickness],
    ["Silkscreen Color", spec.silkscreen_ink_color],
  ].filter(([, value]) => hasMeaningfulValue(value));

  const seen = new Set(baseEntries.map(([label]) => String(label).toLowerCase()));
  const selectedChoices = spec && typeof spec.selected_choices === "object" && spec.selected_choices ? spec.selected_choices : {};
  const selectedOffer = spec && typeof spec.selected_offer === "object" && spec.selected_offer ? spec.selected_offer : null;
  const extraEntries = [
    ...Object.entries(selectedChoices).flatMap(([key, value]) => flattenDisplayEntries(value, humanizeKey(key))),
    ...(selectedOffer ? flattenDisplayEntries(selectedOffer, "Offre selectionnee") : []),
  ];

  for (const [label, value] of extraEntries) {
    const normalized = String(label).toLowerCase();
    if (!seen.has(normalized)) {
      baseEntries.push([label, value]);
      seen.add(normalized);
    }
  }

  return baseEntries;
}

function assemblySpecEntries(spec) {
  if (!spec) return [];
  return [
    ["PCB fourni client", spec.customer_supplies_pcb ? "Oui" : "Non"],
    ["Press-fit", spec.press_fit === null || spec.press_fit === undefined ? "" : spec.press_fit ? "Oui" : "Non"],
    ["Customer Supplies Pcb", spec.customer_supplies_pcb ? "Oui" : "Non"],
    ["Press Fit", spec.press_fit === null || spec.press_fit === undefined ? "" : spec.press_fit ? "Oui" : "Non"],
    ["Cms Component Count", spec.cms_component_count],
    ["Tht Component Count", spec.tht_component_count],
    ["Press Fit Component Count", spec.press_fit_component_count],
  ].filter(([, value]) => hasMeaningfulValue(value));
}

function marginRatioForType(q, type) {
  if (type === "pcb") return Number(q?.pcb_margin_pct || 0);
  if (type === "assembly") return Number(q?.assembly_margin_pct || 0);
  if (type === "components") return Number(q?.components_margin_pct || 0);
  return 0;
}

function subtotalBeforeDiscount(q) {
  const totalHt = Number(q?.total || 0);
  const kind = String(q?.discount_kind || "none").toLowerCase();
  const rawValue = Number(q?.discount_value || 0);

  if (kind === "amount") {
    return totalHt + rawValue;
  }

  if (kind === "pct") {
    const ratio = rawValue > 1 ? rawValue / 100 : rawValue;
    if (ratio >= 1) return totalHt;
    return totalHt / (1 - ratio);
  }

  return totalHt;
}

function commercialAmountForLine(line, q) {
  const rawAmount = Number(line?.amount || 0);
  const type = line?.kind || line?.type || typeFromLabel(line?.label);
  return rawAmount * (1 + marginRatioForType(q, type));
}

function supplierFromType(type, suppliersUsed) {
  const m = suppliersUsed || {};
  if (type === "pcb") return m.pcb || "-";
  if (type === "assembly") return m.assembly || "-";
  if (type === "components") return m.components || "-";
  if (type === "shipping") return m.shipping || "-";
  return "-";
}

function buildTrackingUrl(carrier, number) {
  const n = String(number || "").trim();
  if (!n) return "";
  const c = String(carrier || "").toLowerCase();
  if (c === "fedex") return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`;
  return "";
}

function normalizeProofClientChoice(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "standard" || raw === "ok") return "standard";
  if (["fourni", "provided", "customer_supplied"].includes(raw)) return "Fourni";
  if (["supprimee", "supprimee", "not_assembled", "deleted"].includes(raw)) return "Supprimee";
  if (["remplace", "remplace", "replaced"].includes(raw)) return "Remplace";
  return value || "standard";
}

function proofClientChoiceLabel(value) {
  const choice = normalizeProofClientChoice(value);
  if (choice === "Fourni") return "Fourni";
  if (choice === "Supprimee") return "Supprimee";
  if (choice === "Remplace") return "Remplacee";
  return "Standard";
}

function normalizeProofRow(row) {
  const next = {
    bom_item_id: null,
    refs: "",
    mpn: "",
    supplier: "",
    supplier_ref: "",
    client_choice: "standard",
    original_mpn: "",
    original_supplier: "",
    original_supplier_ref: "",
    replacement_mpn: "",
    reason: "",
    blocking_reason: "",
    ...(row || {}),
  };
  next.client_choice = normalizeProofClientChoice(next.client_choice);
  return next;
}

function proofRowClassName(row) {
  const choice = normalizeProofClientChoice(row?.client_choice);
  if (choice === "Supprimee") return "boBomTraceRowDeleted";
  if (choice === "Fourni") return "boBomTraceRowProvided";
  return "";
}

export default function QuoteDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const access = useStore((s) => s.user.access);
  const apiFetch = useCallback(
    async (url, options = {}) => {
      return fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
          ...(options.headers || {}),
        },
      });
    },
    [access]
  );

  const [pcbMargin, setPcbMargin] = useState("0");
  const [assemblyMargin, setAssemblyMargin] = useState("0");
  const [componentsMargin, setComponentsMargin] = useState("0");

  const [discountKind, setDiscountKind] = useState("none");
  const [discountValue, setDiscountValue] = useState("0");
  const [vatRatePercent, setVatRatePercent] = useState("20");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [lineAmounts, setLineAmounts] = useState({});
  const [followupBomRows, setFollowupBomRows] = useState([]);
  const [pcbSpec, setPcbSpec] = useState(null);
  const [assemblySpec, setAssemblySpec] = useState(null);
  const [bomQuery, setBomQuery] = useState("");
  const [mappedPage, setMappedPage] = useState(1);
  const [mappedPageSize, setMappedPageSize] = useState(25);
  const [trackingCarrier, setTrackingCarrier] = useState("fedex");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [validatedByBackoffice, setValidatedByBackoffice] = useState(false);
  const [trackingSavedMsg, setTrackingSavedMsg] = useState("");
  const [paymentUi, setPaymentUi] = useState(null);
  const [validationUi, setValidationUi] = useState(null);
  const [estimateSending, setEstimateSending] = useState(false);
  const [estimateMsg, setEstimateMsg] = useState("");
  const [orderConfirmationUploading, setOrderConfirmationUploading] = useState(false);
  const [orderConfirmationMsg, setOrderConfirmationMsg] = useState("");
  const [orderConfirmationFile, setOrderConfirmationFile] = useState(null);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [manualInvoiceSending, setManualInvoiceSending] = useState(false);
  const [bankTransferMarking, setBankTransferMarking] = useState(false);
  const [invoiceMarkPaid, setInvoiceMarkPaid] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState("");

  const applyQuotePayload = useCallback((data) => {
    const q = data?.quote || data || null;
    if (!q) return null;
    const nextPaymentUi = data?.payment_ui || null;
    setPaymentUi(nextPaymentUi);
    setValidationUi(data?.validation_ui || null);

    const ls = data?.lines || q.lines || buildLinesFromMeta(q);
    const lsArr = Array.isArray(ls) ? ls : [];

    setQuote(q);
    setPcbMargin(pctFromRatio(q.pcb_margin_pct));
    setAssemblyMargin(pctFromRatio(q.assembly_margin_pct));
    setComponentsMargin(pctFromRatio(q.components_margin_pct));

    setDiscountKind(q.discount_kind || "none");
    setDiscountValue(String(q.discount_value ?? 0));
    setVatRatePercent(String(q.vat_rate_percent ?? 20));
    setLines(lsArr);
    setFollowupBomRows(Array.isArray(data?.followup_bom_rows) ? data.followup_bom_rows.map(normalizeProofRow) : []);
    setPcbSpec(data?.pcb_spec || null);
    setAssemblySpec(data?.assembly_spec || null);

    const initAmounts = {};
    lsArr.forEach((l) => {
      if (l.id != null) initAmounts[l.id] = String(l.amount ?? 0);
    });
    setLineAmounts(initAmounts);
    const metaSync = q?.pricing_meta || q?.meta || {};
    const tr = metaSync?.shipping_tracking || {};
    setTrackingCarrier(tr.carrier || "fedex");
    setTrackingNumber(tr.tracking_number || "");
    setOrderStatus(String(metaSync?.order_status || q?.status || "en_cours"));
    setValidatedByBackoffice(Boolean(q?.is_validated_by_backoffice));
    setInvoiceMarkPaid(
      Boolean(
        String(q?.status || "").toLowerCase() === "paid"
        || nextPaymentUi?.is_paid
        || metaSync?.bank_transfer_paid_at
        || metaSync?.pennylane_invoice_payment_status === "paid"
      )
    );

    return q;
  }, []);

  const loadQuoteDetail = useCallback(async () => {
    const res = await apiFetch(API_QUOTE_DETAIL(quoteId), { method: "GET" });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${t}`.trim());
    }

    const data = await res.json();
    applyQuotePayload(data);
    return data;
  }, [apiFetch, applyQuotePayload, quoteId]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");
    setTrackingSavedMsg("");

      try {
        await loadQuoteDetail();

        if (!alive) return;
      } catch (e) {
        if (alive) setErr(e?.message || "Erreur lors du chargement.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [loadQuoteDetail]);

  async function onSavePricing() {
    if (!quote) return;
    setSaving(true);
    setErr("");
    setTrackingSavedMsg("");

    try {
      const body = {
        pcb_margin_pct: parseDecimalInput(pcbMargin, 0),
        assembly_margin_pct: parseDecimalInput(assemblyMargin, 0),
        components_margin_pct: parseDecimalInput(componentsMargin, 0),
        discount_kind: discountKind,
        discount_value: discountKind === "none" ? 0 : parseDecimalInput(discountValue, 0),
        vat_rate_percent: parseDecimalInput(vatRatePercent, 0),
      };

      const res = await apiFetch(API_QUOTE_PRICING(quote.id), {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }

      const data = await res.json();
      applyQuotePayload(data);
      try {
        await loadQuoteDetail();
      } catch (_) {
      }
    } catch (e) {
      setErr(e?.message || "Erreur pricing.");
    } finally {
      setSaving(false);
    }
  }

  async function onSaveLine(lineId) {
    if (!quote) return;
    const newAmount = parseDecimalInput(lineAmounts[lineId], 0);
    setSaving(true);
    setErr("");
    setTrackingSavedMsg("");
    try {
      const res = await apiFetch(`${API_BASE}/superuser/${quote.id}/lines/${lineId}/`, {
        method: "PUT",
        body: JSON.stringify({ amount: newAmount }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }
      const data = await res.json();
      const q = data.quote || data;
      setQuote((prev) => ({ ...(prev || {}), ...q }));
      const updatedLines = q.lines || lines;
      setLines(Array.isArray(updatedLines) ? updatedLines : lines);
      const initAmounts = {};
      (Array.isArray(updatedLines) ? updatedLines : lines).forEach((l) => {
        if (l.id != null) initAmounts[l.id] = String(l.amount ?? 0);
      });
      setLineAmounts(initAmounts);
    } catch (e) {
      setErr(e?.message || "Erreur sauvegarde ligne.");
    } finally {
      setSaving(false);
    }
  }

  async function onValidate() {
    if (!quote) return;
    const currentValidationUi = validationUi || {};
    if (!currentValidationUi?.can_validate && !currentValidationUi?.is_already_validated) {
      setErr(currentValidationUi?.helper_text || "Crée d'abord le devis Pennylane avant de valider le devis.");
      return;
    }
    setSaving(true);
    setErr("");
    setTrackingSavedMsg("");

    try {
      const statusRes = await apiFetch(API_QUOTE_STATUS(quote.id), {
        method: "POST",
        body: JSON.stringify({ status: "validated" }),
      });

      if (!statusRes.ok) {
        const t = await statusRes.text().catch(() => "");
        throw new Error(`HTTP ${statusRes.status} ${t}`.trim());
      }

      const statusData = await statusRes.json();

      const detailRes = await apiFetch(API_QUOTE_DETAIL(quote.id), {
        method: "PATCH",
        body: JSON.stringify({ is_validated_by_backoffice: true }),
      });

      if (!detailRes.ok) {
        const t = await detailRes.text().catch(() => "");
        throw new Error(`HTTP ${detailRes.status} ${t}`.trim());
      }

      const detailData = await detailRes.json();
      const q2 = detailData.quote || detailData;
      setValidationUi(detailData?.validation_ui || null);
      setQuote((prev) => ({ ...(prev || {}), ...q2, status: statusData.status || q2?.status || "validated" }));
      setValidatedByBackoffice(true);
      setTrackingSavedMsg("Devis validé");
    } catch (e) {
      setErr(e?.message || "Erreur lors de la validation.");
    } finally {
      setSaving(false);
    }
  }


  async function onSaveTracking() {
    if (!quote) return;
    setSaving(true);
    setErr("");
    setTrackingSavedMsg("");
    setInvoiceMsg("");

    try {
      const tracking_url = buildTrackingUrl(trackingCarrier, trackingNumber);

      const nextMeta = {
        ...(quote.pricing_meta || quote.meta || {}),
        shipping_tracking: {
          carrier: trackingCarrier,
          tracking_number: trackingNumber,
          tracking_url,
        },
      };

      const res = await apiFetch(API_QUOTE_DETAIL(quote.id), {
        method: "PATCH",
        body: JSON.stringify({
          pricing_meta: nextMeta,
          shipping_tracking: nextMeta.shipping_tracking,
          order_status: orderStatus,
          force_mark_paid: invoiceMarkPaid,
        }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }

      const data = await res.json();
      const q2 = data.quote || data;
      const pennylane = data?.pennylane || null;
      setPaymentUi(data?.payment_ui || null);
      setValidationUi(data?.validation_ui || null);
      setQuote((prev) => ({ ...(prev || {}), ...q2 }));
      if (pennylane?.status === "error") {
        setInvoiceMsg(`Erreur : ${pennylane?.message || "inconnue"}`);
        setTrackingSavedMsg("Suivi livraison / bordereau d'expédition sauvegardé.");
      } else {
        const invoiceMessage = pennylane?.message || "";
        const nextActionKey = data?.payment_ui?.primary_action?.key || "";
        setInvoiceMsg(invoiceMessage);
        setTrackingSavedMsg(
          invoiceMessage
            ? `Suivi livraison / bordereau d'expédition sauvegardé. ${invoiceMessage}`
            : nextActionKey === "send_invoice"
              ? "Suivi livraison / bordereau d'expédition sauvegardé. Vous pouvez maintenant envoyer la facture."
              : "Suivi livraison / bordereau d'expédition sauvegardé."
        );
      }

      const metaSync = q2?.pricing_meta || q2?.meta || {};
      const tr = metaSync?.shipping_tracking || {};
      setTrackingCarrier(tr.carrier || trackingCarrier || "fedex");
      setTrackingNumber(tr.tracking_number || trackingNumber || "");
      setOrderStatus(String(metaSync?.order_status || orderStatus || q2?.status || "en_cours"));
      setValidatedByBackoffice(Boolean(q2?.is_validated_by_backoffice));
    } catch (e) {
      setErr(e?.message || "Erreur sauvegarde tracking.");
    } finally {
      setSaving(false);
    }
  }


  async function onSendEstimate({ force = false } = {}) {
    if (!quote) return;
    setEstimateSending(true);
    setEstimateMsg("");
    if (force) setTrackingSavedMsg("");
    try {
      const res = await apiFetch(
        API_PENNYLANE_ESTIMATE(quote.id),
        { method: "POST", body: JSON.stringify({ force }) }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || data?.result?.message || `HTTP ${res.status}`);
      }
      const msg = data?.result?.message || "Devis Pennylane envoyé.";
      setEstimateMsg(msg);
      await loadQuoteDetail().catch(() => {});
    } catch (e) {
      setEstimateMsg(`Erreur : ${e?.message || "inconnue"}`);
    } finally {
      setEstimateSending(false);
    }
  }

  async function onUploadOrderConfirmation() {
    if (!quote || !orderConfirmationFile) return;
    setOrderConfirmationUploading(true);
    setOrderConfirmationMsg("");
    setErr("");
    try {
      const fd = new FormData();
      fd.append("order_confirmation_file", orderConfirmationFile);

      const res = await fetch(API_ORDER_CONFIRMATION_FILE(quote.id), {
        method: "POST",
        headers: {
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
        },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      if (data?.quote) {
        setQuote((prev) => ({ ...(prev || {}), ...data.quote }));
      }
      setOrderConfirmationFile(null);
      setOrderConfirmationMsg(data?.message || "Bon de commande téléversé.");
      await loadQuoteDetail().catch(() => {});
    } catch (e) {
      setOrderConfirmationMsg(`Erreur : ${e?.message || "inconnue"}`);
    } finally {
      setOrderConfirmationUploading(false);
    }
  }

  async function onDeleteOrderConfirmation() {
    if (!quote) return;
    setOrderConfirmationUploading(true);
    setOrderConfirmationMsg("");
    setErr("");
    try {
      const res = await apiFetch(API_ORDER_CONFIRMATION_FILE(quote.id), {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      if (data?.quote) {
        setQuote((prev) => ({ ...(prev || {}), ...data.quote }));
      }
      setOrderConfirmationFile(null);
      setOrderConfirmationMsg(data?.message || "Bon de commande supprimé.");
      await loadQuoteDetail().catch(() => {});
    } catch (e) {
      setOrderConfirmationMsg(`Erreur : ${e?.message || "inconnue"}`);
    } finally {
      setOrderConfirmationUploading(false);
    }
  }

  async function onSendInvoice() {
    if (!quote) return;
    setInvoiceSending(true);
    setInvoiceMsg("");
    setErr("");
    try {
      const res = await apiFetch(API_PENNYLANE_INVOICE_SEND(quote.id), {
        method: "POST",
        body: JSON.stringify({ force_mark_paid: invoiceMarkPaid }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || data?.result?.message || `HTTP ${res.status}`);
      }
      const msg = data?.result?.message || "Facture Pennylane envoyée.";
      setInvoiceMsg(msg);
      setPaymentUi(data?.payment_ui || null);
      setValidationUi(data?.validation_ui || null);
      if (data?.quote) {
        setQuote((prev) => ({ ...(prev || {}), ...data.quote }));
      }
      await loadQuoteDetail().catch(() => {});
    } catch (e) {
      setInvoiceMsg(`Erreur : ${e?.message || "inconnue"}`);
    } finally {
      setInvoiceSending(false);
    }
  }

  async function onMarkBankTransferPaid() {
    if (!quote) return;
    setBankTransferMarking(true);
    setInvoiceMsg("");
    setErr("");
    try {
      const res = await apiFetch(API_BANK_TRANSFER_MARK_PAID(quote.id), {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }
      if (data?.quote) {
        setQuote((prev) => ({ ...(prev || {}), ...data.quote }));
      }
      setPaymentUi(data?.payment_ui || null);
      setInvoiceMarkPaid(true);
      setInvoiceMsg("Virement reçu : paiement marqué comme payé.");
      await loadQuoteDetail().catch(() => {});
    } catch (e) {
      setInvoiceMsg(`Erreur : ${e?.message || "inconnue"}`);
    } finally {
      setBankTransferMarking(false);
    }
  }

  async function onMarkManualPaidInvoiceSent() {
    if (!quote) return;
    setManualInvoiceSending(true);
    setInvoiceMsg("");
    setErr("");
    try {
      const res = await apiFetch(API_PENNYLANE_MANUAL_PAID_SENT(quote.id), {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || data?.result?.message || `HTTP ${res.status}`);
      }
      setInvoiceMsg(data?.result?.message || "Facture réglée marquée comme envoyée.");
      setPaymentUi(data?.payment_ui || null);
      setValidationUi(data?.validation_ui || null);
      if (data?.quote) {
        setQuote((prev) => ({ ...(prev || {}), ...data.quote }));
      }
      await loadQuoteDetail().catch(() => {});
    } catch (e) {
      setInvoiceMsg(`Erreur : ${e?.message || "inconnue"}`);
    } finally {
      setManualInvoiceSending(false);
    }
  }

  const pricingMeta = quote?.pricing_meta || quote?.meta || {};
  const orderConfirmationFileUrl = quote?.order_confirmation_file_url || "";
  const orderConfirmationFileName = quote?.order_confirmation_file_name || "";
  const orderConfirmationEmailSentAt = pricingMeta?.customer_order_confirmation_email_sent_at || "";
  const orderConfirmationEmailStatus = pricingMeta?.customer_order_confirmation_email_status || "";
  const orderNumber = quote?.order_number || pricingMeta?.order_number || "";
  const invoiceSentAt = pricingMeta?.pennylane_invoice_sent_at || "";
  const invoiceId = pricingMeta?.pennylane_invoice_id || "";
  const invoicePaymentStatus = String(pricingMeta?.pennylane_invoice_payment_status || "");
  const paymentMode = paymentUi?.payment_mode || "immediate_payment";
  const bankTransferStatus = String(paymentUi?.bank_transfer_status || "").trim().toLowerCase();
  const showBankTransferPaidButton = paymentMode === "bank_transfer" && !paymentUi?.is_paid && bankTransferStatus !== "paid";
  const primaryAction = paymentUi?.primary_action || {
    key: invoiceSentAt ? "save_tracking" : "save_tracking_and_send_invoice",
    label: invoiceSentAt ? "Enregistrer le tracking" : "Enregistrer le tracking",
    description: "En enregistrant le tracking / bordereau d'expédition, le suivi devient visible pour le client.",
  };
  const primaryActionKey = primaryAction?.key || "save_tracking";
  const primaryActionLabel = primaryAction?.label || "Enregistrer";
  const displayedPrimaryActionLabel =
    invoiceMarkPaid && primaryActionKey === "send_invoice"
      ? "Préparer l'envoi manuel Pennylane"
      : invoiceMarkPaid && primaryActionKey === "save_tracking_and_send_invoice"
        ? "Enregistrer le tracking / bordereau"
      : primaryActionLabel;
  const primaryActionDescription = primaryAction?.description || "";
  const invoiceStatusText = paymentUi?.invoice_status_text || (
    invoicePaymentStatus === "manual_paid_invoice_required"
      ? "Action requise : envoyer la facture réglée depuis Pennylane."
      : invoicePaymentStatus === "manual_paid_invoice_sent"
        ? "Facture réglée envoyée manuellement depuis Pennylane."
        : ""
  );
  const invoiceStatusIsManualRequired = invoicePaymentStatus === "manual_paid_invoice_required"
    || Boolean(paymentUi?.manual_paid_invoice_required);
  const invoiceStatusDetails = [
    invoiceId ? <>ID : <span className="mono">{invoiceId}</span></> : null,
    invoicePaymentStatus ? <>Statut : <span className="mono">{invoicePaymentStatusLabel(invoicePaymentStatus)}</span></> : null,
    invoiceSentAt ? <>{new Date(invoiceSentAt).toLocaleString("fr-FR")}</> : null,
  ].filter(Boolean);
  const showManualInvoiceButton = Boolean(paymentUi?.show_manual_invoice_button) || invoicePaymentStatus === "manual_paid_invoice_required";
  const primaryActionBusy =
    primaryActionKey === "send_invoice" ? invoiceSending :
    primaryActionKey === "mark_manual_paid_invoice_sent" ? manualInvoiceSending :
    saving;
  const primaryActionHandler =
    primaryActionKey === "send_invoice" ? onSendInvoice :
    primaryActionKey === "mark_manual_paid_invoice_sent" ? onMarkManualPaidInvoiceSent :
    onSaveTracking;
  const pcbConfigRows = useMemo(() => {
    const rows = [...pcbSpecEntries(pcbSpec), ...assemblySpecEntries(assemblySpec)];
    const seen = new Set();
    return rows.filter(([label]) => {
      const key = String(label || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [pcbSpec, assemblySpec]);
  const decisions = pricingMeta?.line_decisions || {};
  const suppliersUsed = quote?.suppliers_used || pricingMeta?.suppliers_used || {};
  const supplierShippingCost = pricingMeta?.supplier_shipping_cost || {};
  const supplierComponentsShippingCost = Number(pricingMeta?.supplier_components_shipping_cost || 0);
  const supplierShippingBreakdown = Object.entries(supplierShippingCost || {})
    .filter(([, amount]) => Number(amount || 0) > 0)
    .map(([supplier, amount]) => `${supplier}: ${eur(amount)}`)
    .join(" · ");

  const enrichedLines = useMemo(() => {
    const ls = Array.isArray(lines) ? lines : [];
    return ls.map((l) => {
      const baseType = l.kind || l.type || typeFromLabel(l.label);
      const supplier = supplierFromType(baseType, suppliersUsed);
      return {
        ...l,
        _boType: baseType,
        _boSupplier: supplier,
        _commercialAmount: commercialAmountForLine(l, quote),
      };
    });
  }, [lines, quote, suppliersUsed]);

  const commercialSubtotal = useMemo(() => subtotalBeforeDiscount(quote), [quote]);
  const commercialDiscountAmount = useMemo(
    () => Math.max(0, commercialSubtotal - Number(quote?.total || 0)),
    [commercialSubtotal, quote]
  );

  const mappedFiltered = useMemo(() => {
    const rows = Array.isArray(pricingMeta?.mapped) ? pricingMeta.mapped : [];
    const q = String(bomQuery || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const ref = String(r?.ref || "").toLowerCase();
      const mpn = String(r?.mpn || "").toLowerCase();
      const err = String(r?.error || "").toLowerCase();
      const sku = String(r?.supplier_sku || r?.mouser_part_number || r?.supplier_part_number || "").toLowerCase();
      const supplier = String(
        normalizeProofClientChoice(r?.client_choice) === "Fourni" ? "Client" : (r?.supplier || "")
      ).toLowerCase();
      return ref.includes(q) || mpn.includes(q) || err.includes(q) || sku.includes(q) || supplier.includes(q);
    });
  }, [pricingMeta, bomQuery]);

  useEffect(() => {
    setMappedPage(1);
  }, [bomQuery, mappedPageSize]);

  const mappedTotal = mappedFiltered.length;

  const mappedTotalPages = useMemo(() => {
    return Math.max(1, Math.ceil(mappedTotal / mappedPageSize));
  }, [mappedTotal, mappedPageSize]);

  const mappedPageRows = useMemo(() => {
    const start = (mappedPage - 1) * mappedPageSize;
    return mappedFiltered.slice(start, start + mappedPageSize);
  }, [mappedFiltered, mappedPage, mappedPageSize]);

  const proofBomRows = useMemo(() => {
    return (Array.isArray(followupBomRows) ? followupBomRows : [])
      .map(normalizeProofRow)
      .filter((row) => normalizeProofClientChoice(row.client_choice) !== "standard")
      .sort((a, b) => {
        const aDeleted = normalizeProofClientChoice(a.client_choice) === "Supprimee" ? 1 : 0;
        const bDeleted = normalizeProofClientChoice(b.client_choice) === "Supprimee" ? 1 : 0;
        if (aDeleted !== bDeleted) return aDeleted - bDeleted;
        return Number(a.bom_item_id || 0) - Number(b.bom_item_id || 0);
      });
  }, [followupBomRows]);

  useEffect(() => {
    if (mappedPage > mappedTotalPages) setMappedPage(mappedTotalPages);
  }, [mappedPage, mappedTotalPages]);

  if (loading) {
    return (
      <div className="boQD">
        <div className="boCard">
          <div className="boSkeletonRow" />
          <div className="boSkeletonRow" />
          <div className="boSkeletonRow" />
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="boQD">
        <div className="boCard boError">
          <div className="boErrorTitle">Erreur</div>
          <div className="boErrorText">{err}</div>
          <div className="boQDRow">
            <button className="boBtn" onClick={() => window.location.reload()}>
              Réessayer
            </button>
            <button className="boBtn boBtnGhost" onClick={() => navigate(-1)}>
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="boQD">
        <div className="boCard boEmpty">
          <div className="boEmptyTitle">Devis introuvable</div>
          <button className="boBtn" onClick={() => navigate(-1)}>
            Retour
          </button>
        </div>
      </div>
    );
  }

  const status = normalizeStatus(quote.status);
  const trackingUrl = buildTrackingUrl(trackingCarrier, trackingNumber);
  const purchaseData = quote?.components_purchase_data || {};
  const purchaseStatus = normalizeStatus(purchaseData?.status || "draft");
  const quoteValidated = Boolean(
    validatedByBackoffice || ["validated", "paid", "in_progress", "delivered"].includes(status)
  );
  const quoteAcceptedByCustomer = Boolean(quote?.is_validated_by_customer);
  const validationState = (() => {
    const base = validationUi || {
      can_validate: Boolean((quote?.pricing_meta || {}).pennylane_estimate_id) && !quoteValidated,
      is_already_validated: quoteValidated,
      has_pennylane_estimate: Boolean((quote?.pricing_meta || {}).pennylane_estimate_id),
      button_label: quoteValidated ? "Devis déjà validé" : "Valider le devis",
    };
    const hasPennylane = Boolean(base.has_pennylane_estimate);
    return {
      ...base,
      helper_text: hasPennylane
        ? "Le devis Pennylane est prêt. Vous pouvez maintenant valider le devis."
        : "Créez d'abord le devis Pennylane pour pouvoir valider le devis.",
    };
  })();
  const canValidateQuote = Boolean(validationState?.can_validate);
  const validationButtonDisabled = saving || validatedByBackoffice || !canValidateQuote;
  const showTrackingSection = paymentUi ? (Boolean(paymentUi.show_tracking_section) || quoteAcceptedByCustomer) : quoteAcceptedByCustomer;
  const showInvoiceSendButton = Boolean(paymentUi?.show_invoice_send_button);

  return (
    <div className="boQD">
      <div className="boQDTop">
        <div>
          <div className="boQDTitleRow">
            <h1 className="boQDTitle">Devis #{quote.id}</h1>
            <span className={badgeClass(status)}>{status}</span>
          </div>
          <div className="boQDSub">
            Projet : <span className="mono">#{quote.project_id ?? "-"}</span>
          </div>
        </div>

        <div className="boQDTopActions">
          <button className="boBtn boBtnGhost" onClick={() => navigate(-1)}>
            Retour
          </button>

          {/* <button
            className="boBtn boBtnPrimary"
            onClick={onValidate}
            disabled={saving || status !== "draft"}
            title={status !== "draft" ? "Seulement possible sur un devis draft" : "Valider le devis"}
          >
            Valider
          </button> */}
        </div>
      </div>

      <div className="boQDGrid">
        <div className="boCard">
          <div className="boSectionTitle">Résumé</div>

          <div className="boKVRow">
            <div className="boKVKey">Sous-total brut</div>
            <div className="boKVVal mono">{eur(commercialSubtotal)}</div>
          </div>

          {quote.discount_kind && quote.discount_kind !== "none" && (() => {
            const val = Number(quote.discount_value || 0);
            return (
              <div className="boKVRow">
                <div className="boKVKey" style={{ color: "#f87171" }}>
                  Remise ({quote.discount_kind === "pct" ? `${val}%` : `${eur(val)}`})
                </div>
                <div className="boKVVal mono" style={{ color: "#f87171" }}>- {eur(commercialDiscountAmount)}</div>
              </div>
            );
          })()}

          <div className="boKVRow">
            <div className="boKVKey" style={{ fontWeight: 600 }}>Total HT</div>
            <div className="boKVVal mono strong">{eur(quote.total)}</div>
          </div>

          <div className="boKVRow">
            <div className="boKVKey">TVA ({Number(quote.vat_rate_percent || 0).toFixed(0)}%)</div>
            <div className="boKVVal mono">{eur(quote.vat_amount)}</div>
          </div>

          <div className="boKVRow">
            <div className="boKVKey">Total TTC</div>
            <div className="boKVVal mono strong">{eur(quote.total_ttc || quote.total)}</div>
          </div>

          <div className="boDivider" />

          <div className="boKVRow">
            <div className="boKVKey">Marge PCB (%)</div>
            <div className="boKVVal">
              <input className="boInput boInputSmall mono" value={pcbMargin} onChange={(e) => setPcbMargin(e.target.value)} disabled={status !== "draft" || saving} />
            </div>
          </div>

          <div className="boKVRow">
            <div className="boKVKey">Marge Assemblage (%)</div>
            <div className="boKVVal">
              <input className="boInput boInputSmall mono" value={assemblyMargin} onChange={(e) => setAssemblyMargin(e.target.value)} disabled={status !== "draft" || saving} />
            </div>
          </div>

          <div className="boKVRow">
            <div className="boKVKey">Marge Composants (%)</div>
            <div className="boKVVal">
              <input className="boInput boInputSmall mono" value={componentsMargin} onChange={(e) => setComponentsMargin(e.target.value)} disabled={status !== "draft" || saving} />
            </div>
          </div>

          <div className="boDivider" />

          <div className="boKVRow">
            <div className="boKVKey">TVA (%)</div>
            <div className="boKVVal">
              <input className="boInput boInputSmall mono" value={vatRatePercent} onChange={(e) => setVatRatePercent(e.target.value)} inputMode="decimal" disabled={status !== "draft" || saving} />
            </div>
          </div>

          <div className="boKVRow">
            <div className="boKVKey">Remise</div>
            <div className="boKVVal" style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select className="boInput boInputSmall mono" value={discountKind} onChange={(e) => setDiscountKind(e.target.value)} disabled={status !== "draft" || saving}>
                <option value="none">Aucune</option>
                <option value="pct">%</option>
                <option value="amount">€</option>
              </select>
              <input
                className="boInput boInputSmall mono"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                disabled={discountKind === "none" || status !== "draft" || saving}
                inputMode="decimal"
                placeholder={discountKind === "pct" ? "ex: 5" : "ex: 50"}
              />
            </div>
          </div>

          {status === "draft" ? (
            <button
              className="boBtn boBtnPrimary"
              onClick={onSavePricing}
              disabled={saving}
            >
              {saving ? "Sauvegarde..." : "Sauver (marges + TVA + remise)"}
            </button>
          ) : (
            <div className="boHint" style={{ marginTop: 8 }}>
              Modification des marges, de la TVA et de la remise autorisée seulement quand le devis est en statut draft.
            </div>
          )}


          {!!Object.keys(suppliersUsed || {}).length && (
            <div className="boHint" style={{ marginTop: 10 }}>
              <b>Fournisseurs :</b>{" "}
              <span className="mono">
                PCB={suppliersUsed.pcb || "-"} Composants={suppliersUsed.components || "-"} Assemblage={suppliersUsed.assembly || "-"}
              </span>
            </div>
          )}
        </div>

        <div className="boCard">
          <div className="boSectionTitle">Lignes</div>

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th>Libellé</th>
                  <th>Type</th>
                  <th className="right">Montant HT brut</th>
                  {status === "draft" && <th className="right">Modifier</th>}
                </tr>
              </thead>
              <tbody>
                {enrichedLines.map((l, idx) => {
                  const isAssemblyLine = String(l._boType || "").toLowerCase() === "assembly";
                  return (
                    <React.Fragment key={l.id || idx}>
                      <tr>
                        <td>{l.label || l.name || "-"}</td>
                        <td className="mono">{l._boType || "-"}</td>
                        <td className="right mono strong">{eur(l._commercialAmount)}</td>
                        {status === "draft" && l.id != null && (
                          <td className="right" style={{ whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "flex-end" }}>
                              <input
                                className="boInput boInputSmall mono"
                                style={{ width: 90, textAlign: "right" }}
                                value={lineAmounts[l.id] ?? String(l.amount ?? 0)}
                                onChange={(e) => setLineAmounts((prev) => ({ ...prev, [l.id]: e.target.value }))}
                                disabled={saving}
                                inputMode="decimal"
                              />
                              <button className="boBtn" style={{ padding: "2px 8px" }} onClick={() => onSaveLine(l.id)} disabled={saving}>OK</button>
                            </div>
                          </td>
                        )}
                        {status === "draft" && l.id == null && <td />}
                      </tr>
                      {isAssemblyLine && supplierComponentsShippingCost > 0 ? (
                        <tr className="boSupplierShippingRow">
                          <td colSpan={status === "draft" ? 4 : 3}>
                            <div className="boSupplierShippingInline">
                              <div>
                                <div className="boSupplierShippingTitle">Frais de port fournisseurs composants inclus dans Assemblage</div>
                                {supplierShippingBreakdown ? (
                                  <div className="boSupplierShippingBreakdown">{supplierShippingBreakdown}</div>
                                ) : null}
                              </div>
                              <div className="boSupplierShippingAmount mono">{eur(supplierComponentsShippingCost)}</div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="boDivider" style={{ margin: "14px 0" }} />

          <div className="boSectionTitle" style={{ marginBottom: 8 }}>Configuration PCB du client</div>
          {pcbConfigRows.length ? (
            <div className="boTableWrap">
              <table className="boTable">
                <tbody>
                  {pcbConfigRows.map(([label, value], index) => (
                    <tr key={`${label}-${index}`}>
                      <td>{label}</td>
                      <td className="mono right">{hasMeaningfulValue(value) ? String(value) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="boHint">Aucune configuration PCB disponible.</div>
          )}

          <div className="boDivider" style={{ margin: "14px 0" }} />

          <div className="boSectionTitle" style={{ marginBottom: 8 }}>Validation du devis</div>
          <div className="boHint">
            {validationState?.helper_text || "Validez d'abord le devis pour débloquer le suivi livraison."}
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              opacity: canValidateQuote || validatedByBackoffice ? 1 : 0.48,
              filter: canValidateQuote || validatedByBackoffice ? "none" : "grayscale(0.35)",
            }}
          >
            <button
              className="boBtn boBtnPrimary"
              onClick={onValidate}
              disabled={validationButtonDisabled}
              title={
                validatedByBackoffice
                  ? "Le devis est déjà validé."
                  : canValidateQuote
                    ? "Valider le devis"
                    : "Créez d'abord le devis Pennylane"
              }
            >
              {saving ? "Sauvegarde..." : validationState?.button_label || (validatedByBackoffice ? "Devis déjà validé" : "Valider le devis")}
            </button>
            {validatedByBackoffice ? (
              <span className="boHint" style={{ color: "#86efac" }}>
                Devis validé.
              </span>
            ) : !canValidateQuote ? (
              <span className="boHint" style={{ color: "#fbbf24" }}>
                Validation verrouillée jusqu'à la création du devis Pennylane.
              </span>
            ) : null}
          </div>

          {quoteAcceptedByCustomer || status === "paid" ? (
            <>
              <div className="boDivider" style={{ margin: "14px 0" }} />
              <div className="boSectionTitle" style={{ marginBottom: 8 }}>Achats composants</div>
              {/* <div className="boHint">
                Préparez la commande composants groupée par fournisseur, avec quantités nécessaires, quantités à commander et double confirmation.
              </div> */}
              <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button className="boBtn boBtnPrimary" onClick={() => navigate(`/bo/quotes/${quote.id}/purchases`)}>
                  Ouvrir les achats composants
                </button>
                <span className="boHint" style={{ color: "#cbd5e1" }}>
                  Statut achats : <span className="mono">{purchaseStatus}</span>
                </span>
              </div>
            </>
          ) : null}

          <div className="boDivider" style={{ margin: "14px 0" }} />

          {/* ── Pennylane Devis ── */}
          {(() => {
            const estimateId = (quote?.pricing_meta || {}).pennylane_estimate_id || "";
            const estimateAt = (quote?.pricing_meta || {}).pennylane_estimate_created_at || "";
            const estimateFileUrl = (quote?.pricing_meta || {}).pennylane_estimate_file_url || "";
            const alreadySent = !!estimateId;
            return (
              <>
                <div className="boSectionTitle" style={{ marginBottom: 8 }}>Devis Pennylane</div>
                <div className="boHint">
                  Génère un vrai devis dans Pennylane avec le détail des lignes. La facture est envoyée séparément.
                </div>
                {alreadySent && (
                  <div className="boHint" style={{ marginTop: 8, color: "#86efac" }}>
                    Devis créé - ID : <span className="mono">{estimateId}</span>
                    {estimateAt && <> - {new Date(estimateAt).toLocaleString("fr-FR")}</>}
                  </div>
                )}
                {estimateFileUrl && (
                  <div style={{ marginTop: 8 }}>
                    <a
                      href={estimateFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="boBtn boBtnGhost"
                      style={{ textDecoration: "none", display: "inline-block" }}
                    >
                      Voir le devis PDF
                    </a>
                  </div>
                )}
                {estimateMsg && (
                  <div className="boHint" style={{ marginTop: 8, color: estimateMsg.startsWith("Erreur") ? "#f87171" : "#86efac" }}>
                    {estimateMsg}
                  </div>
                )}
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {!alreadySent && (
                    <button
                      className="boBtn boBtnPrimary"
                      onClick={() => onSendEstimate({ force: false })}
                      disabled={estimateSending}
                    >
                      {estimateSending ? "Création..." : "Créer le devis Pennylane"}
                    </button>
                  )}
                  {alreadySent && (
                    <button
                      className="boBtn"
                      onClick={() => onSendEstimate({ force: true })}
                      disabled={estimateSending}
                      title="Recrée un nouveau devis Pennylane (remplace l'ID précédent)"
                    >
                      {estimateSending ? "Création..." : "Régénérer le devis"}
                    </button>
                  )}
                </div>

                <div className="boDivider" style={{ margin: "14px 0" }} />
                {/* <div className="boSectionTitle" style={{ marginBottom: 8 }}>Facture Pennylane</div>
                <div className="boHint">
                  La facture est envoyée lors de l'enregistrement du tracking / bordereau d'expédition. Si le client a déjà payé, elle part en statut <span className="mono">payée</span>; sinon elle part en statut <span className="mono">à payer</span>, puis une version <span className="mono">payée</span> est renvoyée automatiquement dès réception du paiement.
                </div> */}
                {/* {invoiceSentAt ? (
                  <div className="boHint" style={{ marginTop: 8, color: "#86efac" }}>
                    Facture envoyée
                    {invoiceId ? <> - ID : <span className="mono">{invoiceId}</span></> : null}
                    {invoicePaymentStatus ? <> - Statut : <span className="mono">{invoicePaymentStatus === "paid" ? "payee" : "a_payer"}</span></> : null}
                    {invoiceSentAt ? <> - {new Date(invoiceSentAt).toLocaleString("fr-FR")}</> : null}
                  </div>
                ) : null} */}
                {/* {invoicePaidEmailSentAt && invoicePaidEmailSentAt !== invoiceSentAt ? (
                  <div className="boHint" style={{ marginTop: 8, color: "#86efac" }}>
                    Version payée renvoyée automatiquement - {new Date(invoicePaidEmailSentAt).toLocaleString("fr-FR")}
                  </div>
                ) : null} */}
                {/* {invoiceMsg ? (
                  <div className="boHint" style={{ marginTop: 8, color: invoiceMsg.startsWith("Erreur") ? "#f87171" : "#86efac" }}>
                    {invoiceMsg}
                  </div>
                ) : null} */}
              </>
            );
          })()}

          <div className="boDivider" style={{ margin: "14px 0" }} />
          <div className="boSectionTitle" style={{ marginBottom: 8 }}>Bon de commande client</div>
          <div className="boHint">
            Téléverse ici le PDF de confirmation de commande. Il sera envoyé automatiquement au client au moment de l'acceptation du devis.
          </div>

          {orderConfirmationFileUrl ? (
            <div className="boHint" style={{ marginTop: 8, color: "#86efac" }}>
              Fichier chargé :{" "}
              <a
                href={orderConfirmationFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mono"
                style={{ color: "#86efac", textDecoration: "none" }}
              >
                {orderConfirmationFileName || "Voir le fichier"}
              </a>
              {orderConfirmationEmailSentAt ? <> - envoyé au client le {new Date(orderConfirmationEmailSentAt).toLocaleString("fr-FR")}</> : null}
              {!orderConfirmationEmailSentAt && orderConfirmationEmailStatus === "pending_acceptance" ? <> - sera envoyé à l'acceptation client</> : null}
            </div>
          ) : (
            <div className="boHint" style={{ marginTop: 8 }}>
              Aucun bon de commande téléversé pour l'instant.
            </div>
          )}

          {orderConfirmationMsg ? (
            <div className="boHint" style={{ marginTop: 8, color: orderConfirmationMsg.startsWith("Erreur") ? "#f87171" : "#86efac" }}>
              {orderConfirmationMsg}
            </div>
          ) : null}

          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setOrderConfirmationFile(e.target.files?.[0] || null)}
              disabled={orderConfirmationUploading}
            />
            <button
              className="boBtn boBtnPrimary"
              onClick={onUploadOrderConfirmation}
              disabled={orderConfirmationUploading || !orderConfirmationFile}
            >
              {orderConfirmationUploading ? "Téléversement..." : "Téléverser le bon de commande"}
            </button>
            {orderConfirmationFileUrl ? (
              <button
                className="boBtn boBtnGhost"
                onClick={onDeleteOrderConfirmation}
                disabled={orderConfirmationUploading}
              >
                Supprimer le fichier
              </button>
            ) : null}
          </div>

          <div className="boDivider" style={{ margin: "14px 0" }} />
          <div className="boSectionTitle" style={{ marginBottom: 8 }}>Suivi livraison</div>

          {(showTrackingSection || showInvoiceSendButton || showManualInvoiceButton) ? (
            <>
              <div className="boHint">
                {primaryActionDescription || (
                  paymentMode === "bank_transfer"
                    ? "Flux virement bancaire : enregistrez d'abord le tracking, puis envoyez la facture au client quand le dossier est prêt."
                    : invoiceSentAt
                      ? "Paiement immédiat : la facture a été envoyée. Renseignez maintenant le tracking."
                      : "Paiement immédiat : renseignez le tracking et envoyez la facture au client."
                )}
              </div>

              {invoiceStatusText ? (
                <>
                  <div
                    className="boHint"
                    style={{
                      marginTop: 10,
                      color: invoiceStatusIsManualRequired ? "#facc15" : "#86efac",
                      fontWeight: 800,
                    }}
                  >
                    {invoiceStatusText}
                  </div>
                  {invoiceStatusDetails.length ? (
                    <div className="boHint" style={{ marginTop: 6, color: "#86efac" }}>
                      {invoiceStatusDetails.map((part, index) => (
                        <React.Fragment key={index}>
                          {index > 0 ? " - " : null}
                          {part}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}

              <div className="boHint" style={{ marginTop: 10 }}>
                Numéro de commande interne : <span className="mono">{orderNumber || "Génération en cours..."}</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "220px 1fr 1fr", gap: 12, alignItems: "end", marginTop: 12 }}>
                <label className="boLabel">
                  Transporteur
                  <select
                    className="boInput mono"
                    value={trackingCarrier}
                    onChange={(e) => setTrackingCarrier(e.target.value)}
                    disabled={saving}
                  >
                    <option value="fedex">FedEx</option>
                  </select>
                </label>

                <label className="boLabel">
                  Numéro de suivi
                  <input
                    className="boInput mono"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="ex: 123456789012"
                    disabled={saving}
                  />
                </label>

                <label className="boLabel">
                  Statut commande
                  <select
                    className="boInput mono"
                    value={orderStatus}
                    onChange={(e) => setOrderStatus(e.target.value)}
                    disabled={saving}
                  >
                    <option value="en_cours">en_cours</option>
                    <option value="expedie">expédié</option>
                    <option value="delivre">délivré</option>
                  </select>
                </label>
              </div>

              {trackingSavedMsg ? <div className="boHint" style={{ marginTop: 10, color: "#86efac" }}>{trackingSavedMsg}</div> : null}
              {invoiceMsg ? (
                <div className="boHint" style={{ marginTop: 10, color: invoiceMsg.startsWith("Erreur") ? "#f87171" : "#86efac" }}>
                  {invoiceMsg}
                </div>
              ) : null}

              <div className="boHint" style={{ marginTop: 10 }}>
                Lien tracking :{" "}
                {trackingUrl ? (
                  <a href={trackingUrl} target="_blank" rel="noreferrer" className="mono" style={{ color: "#f8fafc", textDecoration: "none" }}>
                    {trackingUrl}
                  </a>
                ) : (
                  <span className="mono">-</span>
                )}
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <button
                  className="boBtn boBtnPrimary"
                  onClick={primaryActionHandler}
                  disabled={primaryActionBusy}
                >
                  {primaryActionBusy ? (
                    primaryActionKey === "send_invoice" ? "Envoi..." :
                    primaryActionKey === "mark_manual_paid_invoice_sent" ? "Confirmation..." :
                    "Sauvegarde..."
                  ) : displayedPrimaryActionLabel || "Enregistrer le tracking"}
                </button>
                {!showManualInvoiceButton && !paymentUi?.manual_paid_invoice_sent ? (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, opacity: 0.9 }}>
                    <input
                      type="checkbox"
                      checked={invoiceMarkPaid}
                      onChange={(e) => setInvoiceMarkPaid(e.target.checked)}
                      disabled={saving || invoiceSending}
                    />
                    Client déjà réglé
                  </label>
                ) : null}
                {invoiceMarkPaid && !showManualInvoiceButton && !paymentUi?.manual_paid_invoice_sent && (
                  <button
                    className="boBtn boBtnPrimary"
                    onClick={onSendInvoice}
                    disabled={invoiceSending}
                  >
                    {invoiceSending ? "Enregistrement..." : "Préparer l'envoi manuel Pennylane"}
                  </button>
                )}
                {showManualInvoiceButton && primaryActionKey !== "mark_manual_paid_invoice_sent" ? (
                  <button
                    className="boBtn boBtnPrimary"
                    onClick={onMarkManualPaidInvoiceSent}
                    disabled={manualInvoiceSending}
                  >
                    {manualInvoiceSending ? "Confirmation..." : "Marquer la facture réglée comme envoyée"}
                  </button>
                ) : null}
                {showBankTransferPaidButton ? (
                  <button
                    className="boBtn boBtnPrimary"
                    onClick={onMarkBankTransferPaid}
                    disabled={bankTransferMarking}
                  >
                    {bankTransferMarking ? "Validation..." : "Marquer le virement reçu"}
                  </button>
                ) : null}
              </div>
            </>
          ) : quoteValidated ? (
            <div className="boHint" style={{ marginTop: 6 }}>
              En attente de l'acceptation du devis client. Le suivi livraison sera débloqué dès que le devis sera accepté.
            </div>
          ) : (
            <div className="boHint" style={{ marginTop: 6 }}>
            </div>
          )}
        </div>
      </div>

      {/* BOM MAPPED */}
      <div className="boCard" style={{ marginTop: 16 }}>
        <div className="boSectionTitle" style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
          <span>BOM mappée</span>
          <span className="mono" style={{ opacity: 0.85 }}>
            {mappedTotal} lignes
          </span>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <input
            className="boInput mono"
            style={{ flex: 1 }}
            placeholder="Rechercher (mpn, réf, erreur, sku, fournisseur)..."
            value={bomQuery}
            onChange={(e) => setBomQuery(e.target.value)}
          />

          <select
            className="boInput boInputSmall mono"
            value={mappedPageSize}
            onChange={(e) => setMappedPageSize(Number(e.target.value) || 25)}
            title="Lignes par page"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button className="boBtn" onClick={() => navigate(`/bo/quotes/${quote.id}/followup`)}>
            Fiche suiveuse
          </button>
        </div>

        <div className="boTableWrap">
          <table className="boTable">
           <thead>
  <tr>
    <th>RÉF</th>
    <th>MPN</th>
    <th>FOURNISSEUR</th>
    <th className="right">PRIX UNITAIRE</th>
    <th>MOTIF</th>
  </tr>
</thead>

<tbody>
  {mappedPageRows.map((r, idx) => {
    const mpn = r?.mpn || "";
    const d = decisions?.[mpn] || null;
    const chosen = d?.chosen || null;
    const isCustomerSupplied = normalizeProofClientChoice(r?.client_choice) === "Fourni";

    const supplier = isCustomerSupplied ? "Client" : (chosen?.supplier || r?.supplier || "-");
    const unitPrice = chosen?.unit_price != null ? eur3(chosen.unit_price) : "-";
    const error = !chosen ? (d?.reason || r?.error || "-") : "-";

    return (
      <tr key={`${mpn || "x"}-${r?.ref || "y"}-${idx}`}>
        <td className="mono">{r?.ref || "-"}</td>
        <td className="mono">{mpn || "-"}</td>
        <td className="mono">{supplier}</td>
        <td className="right mono">{unitPrice}</td>
        <td className="mono">{error}</td>
      </tr>
    );
  })}

  {!mappedPageRows.length && (
    <tr>
      <td colSpan={5} style={{ opacity: 0.8 }}>
        Aucune ligne.
      </td>
    </tr>
  )}
</tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, alignItems: "center" }}>
          <div className="mono" style={{ opacity: 0.8 }}>
            page {mappedPage} / {mappedTotalPages}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button className="boBtn boBtnGhost" disabled={mappedPage <= 1} onClick={() => setMappedPage(1)}>
              {"<<"}
            </button>
            <button className="boBtn boBtnGhost" disabled={mappedPage <= 1} onClick={() => setMappedPage((p) => Math.max(1, p - 1))}>
              {"<"}
            </button>
            <button className="boBtn boBtnGhost" disabled={mappedPage >= mappedTotalPages} onClick={() => setMappedPage((p) => Math.min(mappedTotalPages, p + 1))}>
              {">"}
            </button>
            <button className="boBtn boBtnGhost" disabled={mappedPage >= mappedTotalPages} onClick={() => setMappedPage(mappedTotalPages)}>
              {">>"}
            </button>
          </div>
        </div>

        {proofBomRows.length ? (
          <>
            <div className="boDivider" style={{ margin: "14px 0" }} />
            <div className="boSectionTitle" style={{ marginBottom: 8 }}>Modifications client BOM</div>
            <div className="boHint">
Les références supprimées restent visibles en bas afin de conserver une trace des modifications effectuées par le client.            </div>

            <div className="boTableWrap" style={{ marginTop: 12 }}>
              <table className="boTable">
                <thead>
                  <tr>
                    <th>Repères</th>
                    <th>MPN actif</th>
                    <th>Fournisseur</th>
                    <th>Réf fournisseur</th>
                    <th>Choix client</th>
                    <th>Ancien MPN</th>
                    <th>Anc. fournisseur</th>
                    <th>Anc. ref fournisseur</th>
                    <th>Nouveau MPN</th>
                    <th>Motif</th>
                  </tr>
                </thead>
                <tbody>
                  {proofBomRows.map((row, idx) => (
                    <tr key={`${row.bom_item_id || "proof"}-${idx}`} className={proofRowClassName(row)}>
                      <td className="mono">{row.refs || "-"}</td>
                      <td className={`mono ${normalizeProofClientChoice(row.client_choice) === "Remplacé" ? "boBomTraceCellNew" : ""}`}>{row.mpn || "-"}</td>
                      <td className="mono">{row.supplier || "-"}</td>
                      <td className={`mono ${normalizeProofClientChoice(row.client_choice) === "Remplacé" ? "boBomTraceCellNew" : ""}`}>{row.supplier_ref || "-"}</td>
                      <td className="mono">{proofClientChoiceLabel(row.client_choice)}</td>
                      <td className={`mono ${normalizeProofClientChoice(row.client_choice) === "Remplacé" ? "boBomTraceCellOld" : ""}`}>{row.original_mpn || "-"}</td>
                      <td className={`mono ${normalizeProofClientChoice(row.client_choice) === "Remplacé" ? "boBomTraceCellOld" : ""}`}>{row.original_supplier || "-"}</td>
                      <td className={`mono ${normalizeProofClientChoice(row.client_choice) === "Remplacé" ? "boBomTraceCellOld" : ""}`}>{row.original_supplier_ref || "-"}</td>
                      <td className={`mono ${normalizeProofClientChoice(row.client_choice) === "Remplacé" ? "boBomTraceCellNew" : ""}`}>{row.replacement_mpn || "-"}</td>
                      <td>{row.reason || row.blocking_reason || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}





















